import { TranslatableError } from '../../../translation/translatable-error.js';
import * as filterFactory from '../factory.js';
import { getDefaultBaseFilterConfig, simplifyFilterConfig } from '../filter-config-utils.js';
import { createAttributeFromFilterJaql, createMeasureFromFilterJaql, } from './attribute-measure-util.js';
import { createAttributeFilterFromConditionFilterJaql, createMeasureFilterFromConditionFilterJaql, } from './condition-filter-util.js';
import { extractFilterTypeFromFilterJaql } from './filter-types-util.js';
import { FILTER_TYPES, } from './types.js';
/**
 * Creates a generic filter (aka pass-through JAQL filter) if the JAQL cannot be translated to a specific filter type.
 *
 * @param jaql - The JAQL object.
 * @param guid - Optional GUID for the filter
 * @returns A generic Filter object.
 */
export const createGenericFilter = (jaql, guid) => {
    const baseFilter = {
        config: Object.assign(Object.assign({}, getDefaultBaseFilterConfig()), { guid, originalFilterJaql: jaql }),
        jaql: (nested) => {
            if (nested) {
                return jaql;
            }
            return {
                jaql,
                panel: 'scope',
            };
        },
        attribute: {
            id: jaql.dim,
        },
        type: 'filter',
    };
    return Object.assign({}, baseFilter);
};
/**
 * Creates a filter that includes all members of the attribute.
 *
 * @param attribute - The attribute.
 * @param guid - GUID for the filter
 * @returns The created Filter object.
 */
export const createFilterIncludeAll = (attribute, guid) => {
    // empty members array is interpreted by server as all members are included
    return filterFactory.members(attribute, [], { guid });
};
/**
 * Creates a filter from a specific items filter JAQL object.
 *
 * @param attribute - attribute
 * @param specificItemsFilterJaql - Specific Items Filter Jaql
 * @param guid - GUID for the filter
 * @returns Filter object
 */
export const createFilterFromSpecificItemsFilterJaql = (attribute, specificItemsFilterJaql, guid, multiSelection) => {
    const deactivatedMembers = getDeactivatedMembersFromFilterJaql(specificItemsFilterJaql);
    const activeMembers = getActiveMembersFromFilterJaql(specificItemsFilterJaql, deactivatedMembers);
    const config = simplifyFilterConfig({
        guid,
        excludeMembers: false,
        enableMultiSelection: multiSelection !== null && multiSelection !== void 0 ? multiSelection : true,
        deactivatedMembers,
    });
    return filterFactory.members(attribute, activeMembers, config);
};
function getDeactivatedMembersFromFilterJaql(filterJaql) {
    var _a, _b, _c, _d;
    return ((_a = filterJaql.filter) === null || _a === void 0 ? void 0 : _a.turnedOff) ? (_d = (_c = (_b = filterJaql.filter) === null || _b === void 0 ? void 0 : _b.exclude) === null || _c === void 0 ? void 0 : _c.members) !== null && _d !== void 0 ? _d : [] : [];
}
function getActiveMembersFromFilterJaql(filterJaql, deactivatedMembers) {
    const allMembers = filterJaql.members;
    return deactivatedMembers
        ? allMembers.filter((member) => !deactivatedMembers.includes(member))
        : allMembers;
}
/**
 * Creates a filter from a date range filter JAQL object.
 *
 * @param attribute - attribute
 * @param rangeFilterJaql - Range Filter Jaql
 * @param guid - GUID for the filter
 * @returns Filter object
 */
export const createFilterFromDateRangeFilterJaql = (attribute, rangeFilterJaql, guid) => {
    return filterFactory.dateRange(attribute, rangeFilterJaql.from, rangeFilterJaql.to, { guid });
};
/**
 * Creates a filter from a numeric range filter JAQL object.
 *
 * @param attribute - attribute
 * @param rangeFilterJaql - Range Filter Jaql
 * @param guid - GUID for the filter
 * @returns Filter object
 */
export const createFilterFromNumericRangeJaql = (attribute, rangeFilterJaql, guid) => {
    return filterFactory.between(attribute, rangeFilterJaql.from, rangeFilterJaql.to, { guid });
};
/**
 * Creates a filter from a period filter JAQL object.
 *
 * @param attribute - attribute
 * @param periodFilterJaql - Period Filter Jaql
 * @param guid - GUID for the filter
 * @returns Filter object
 */
export const createFilterFromPeriodFilterJaql = (attribute, periodFilterJaql, guid) => {
    if (periodFilterJaql.last) {
        return filterFactory.dateRelativeTo(attribute, periodFilterJaql.last.offset, periodFilterJaql.last.count, periodFilterJaql.last.anchor, { guid });
    }
    else {
        return filterFactory.dateRelativeFrom(attribute, periodFilterJaql.next.offset, periodFilterJaql.next.count, periodFilterJaql.next.anchor, { guid });
    }
};
/**
 * Creates a filter from a custom filter JAQL object.
 *
 * @param attribute - attribute
 * @param customFilterJaql - Custom Filter Jaql
 * @param guid - GUID for the filter
 * @returns Filter object
 */
export const createFilterFromCustomFilterJaql = (attribute, customFilterJaql, guid) => {
    return filterFactory.customFilter(attribute, customFilterJaql, {
        guid,
    });
};
/**
 * Creates a filter from a filter JAQL object.
 *
 * @param jaql - The filter JAQL object.
 * @param guid - GUID for the filter
 * @returns Filter object.
 */
export const createFilterFromJaqlInternal = (jaql, guid) => {
    try {
        if ('formula' in jaql) {
            // generic pass-through JAQL filter will be used instead
            throw new TranslatableError('errors.filter.formulaFiltersNotSupported', {
                filter: JSON.stringify(jaql),
            });
        }
        const filterJaqlWrapperWithType = extractFilterTypeFromFilterJaql(jaql, jaql.datatype);
        const { filter: filterJaqlWithType } = filterJaqlWrapperWithType;
        const { filterType } = filterJaqlWithType;
        const attribute = createAttributeFromFilterJaql(jaql);
        const measure = createMeasureFromFilterJaql(jaql);
        switch (filterType) {
            case FILTER_TYPES.INCLUDE_ALL:
                return createFilterIncludeAll(attribute, guid);
            case FILTER_TYPES.SPECIFIC_ITEMS:
                return createFilterFromSpecificItemsFilterJaql(attribute, filterJaqlWithType, guid, filterJaqlWithType.multiSelection);
            case FILTER_TYPES.CONDITION:
                if (measure) {
                    return createMeasureFilterFromConditionFilterJaql(measure, filterJaqlWithType, guid);
                }
                else {
                    return createAttributeFilterFromConditionFilterJaql(attribute, filterJaqlWithType, guid);
                }
            case FILTER_TYPES.DATE_RANGE:
                return createFilterFromDateRangeFilterJaql(attribute, filterJaqlWithType, guid);
            case FILTER_TYPES.PERIOD:
                return createFilterFromPeriodFilterJaql(attribute, filterJaqlWithType, guid);
            case FILTER_TYPES.NUMERIC_RANGE:
                return createFilterFromNumericRangeJaql(attribute, filterJaqlWithType, guid);
            case FILTER_TYPES.ADVANCED:
                return createFilterFromCustomFilterJaql(attribute, filterJaqlWithType, guid);
            case FILTER_TYPES.INVALID:
                return createGenericFilter(jaql, guid);
        }
    }
    catch (e) {
        // if a filter type is untranslatable, fall back to the generic pass-through JAQL filter
        console.debug('Fall back to generic pass-through JAQL filter due to filter translation error:', e);
    }
    return createGenericFilter(jaql, guid);
};
//# sourceMappingURL=filter-from-jaql-util.js.map